import { useContext, useMemo } from "react";

import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { UniversalBenchmarksContext } from "../context";

import { useUniversalBenchmarksRow } from "./use-universal-benchmarks-row";

interface Args {
  level: string;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  breakdowns: Record<string, string>[] | null;
  filters: { breakdown: string; value: string }[];
  metric?: { id: string; isLtv?: boolean };
}

export const useUniversalBenchmarks = ({
  level,
  attributionModel,
  attributionWindow,
  accountingMode,
  breakdowns,
  filters,
  metric,
}: Args) => {
  const context = useContext(UniversalBenchmarksContext);

  if (!context) {
    throw new Error(
      "useUniversalBenchmarksRow can only be used inside UniversalBenchmarksContext",
    );
  }

  const universalBenchmarksRow = useUniversalBenchmarksRow({
    level,
    attributionModel,
    attributionWindow: metric?.isLtv ? "infinity" : attributionWindow,
    accountingMode,
    breakdowns,
    filters,
  });

  const { adoptedBenchmark } = context;

  return useMemo(() => {
    if (!metric) {
      return null;
    }

    if (!adoptedBenchmark) {
      return null;
    }

    if (!adoptedBenchmark.selectedMetricIds.includes(metric.id)) {
      return null;
    }

    if (!universalBenchmarksRow) {
      return null;
    }

    const row = universalBenchmarksRow.benchmarks.find(
      (e) => e.metric?.metadata.overviewPageMetricId === metric.id,
    );

    return {
      status: universalBenchmarksRow.status,
      value: row?.value ?? null,
      metric: row?.metric ?? null,
    };
  }, [universalBenchmarksRow, adoptedBenchmark, metric]);
};
